import { ReactNode, useCallback, useContext } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import "./EventAgendaPublic.scss";
import moment from "moment";
import EventDaysScroller from "modules/eventAgenda/EventDaysScroller";
import { Provider as ThemeProvider } from "@remo-co/ui-core/src/components/Provider";
import { Box } from "@remo-co/ui-core/src/components/Box";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { PageLoader } from "@remo-co/ui-core/src/components/PageLoader";
import { RemoTheme } from "@remo-co/ui-core/theme";
import getEventLink from "modules/manageEvent/utils/getEventLink";
import { useI18n } from "i18n";
import { selectIsEventManager } from "modules/event/selectors";
import { IEventAgenda } from "modules/eventAgenda/types";
import { StyledEngineProvider } from "@remo-co/ui-core/src/components/StyledEngineProvider";
import EmptyAgenda from "./assets/empty-state-agenda.svg";
import { EVENT_AGENDA_CONTEXT } from "../eventAgenda/context/EventAgendaContext";
import EventAgendaEnd from "./EventAgendaEnd";
import AgendaItem from "./AgendaItem";
import AgendaIcon from "./assets/agenda-grey.svg";
import ModalWindow from "../common/modalWindow/ModalWindow";

interface IEventAgendaPublicProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const EventAgendaPublic = ({ isOpen, setIsOpen }: IEventAgendaPublicProps) => {
  const {
    state,
    eventData: event,
    isLoading,
  } = useContext(EVENT_AGENDA_CONTEXT);
  const agendaItems = state && state.agendaItems ? state.agendaItems : null;
  const { t } = useI18n(["common", "eventAgenda"]);
  const hasEventManagerAccess = useSelector(selectIsEventManager);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  const renderCards = useCallback(() => {
    let timeOffset = 0;
    const eventAgendaItems: ReactNode[] = [];

    if (!agendaItems || !event) {
      return eventAgendaItems;
    }

    agendaItems.forEach((item: IEventAgenda) => {
      eventAgendaItems.push(
        <AgendaItem
          key={item.id}
          item={item}
          offset={timeOffset}
          event={event}
        />,
      );
      timeOffset += item.duration || 0;
    });

    return eventAgendaItems;
  }, [agendaItems, event]);

  const buildEmptyState = useCallback(
    () => (
      <Box className="ea-empty">
        <img src={EmptyAgenda} alt={t("eventAgenda:event.agenda.empty")} />
        <Typography component="div" color="inherit">
          {t("eventAgenda:no.presentations.found")}
        </Typography>
        {hasEventManagerAccess ? (
          <Button<typeof Link>
            variant="secondary"
            color="blue"
            to={getEventLink("agenda", event?.id)}
            component={Link}
          >
            {t("eventAgenda:button.event.agenda")}
          </Button>
        ) : null}
      </Box>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [event, agendaItems],
  );

  const renderAgendaContainer = () => {
    if (isLoading) {
      return (
        <PageLoader
          fullScreen
          message={t("eventAgenda:loading.agenda") as string}
        />
      );
    }

    if (agendaItems && agendaItems.size) {
      return (
        <div className="items-ctn">
          {renderCards()}
          <EventAgendaEnd theme="light" />
        </div>
      );
    }

    return buildEmptyState();
  };

  if (!event) {
    return null;
  }

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={RemoTheme.light}>
        <ModalWindow open={isOpen} onClose={handleClose}>
          <div className="public-agenda-ctn">
            <div className="heading">
              <div className="icon beta">
                <img src={AgendaIcon} alt={t("eventAgenda:agenda")} />
              </div>
              <div className="right-col">
                <Typography variant="h2">{t("eventAgenda:agenda")}</Typography>
                <Typography className="description">{event.name}</Typography>
              </div>
            </div>
            <div className="times">
              <div className="time-item">
                <Typography className="time-title">
                  {t("eventAgenda:date")}
                </Typography>
                <Typography className="time-val">
                  {moment(event.startTime).format("MMM Do")}
                </Typography>
              </div>
              <div className="time-item">
                <Typography className="time-title">
                  {t("eventAgenda:start.time")}
                </Typography>
                <Typography className="time-val">
                  {moment(event.startTime).format("hh:mm A")}
                </Typography>
              </div>
              <div className="time-item">
                <Typography className="time-title">
                  {t("eventAgenda:end.time")}
                </Typography>
                <Typography className="time-val">
                  {moment(event.endTime).format("hh:mm A")}
                </Typography>
              </div>
            </div>
            <div className="divider" />
            <EventDaysScroller />
            {renderAgendaContainer()}
          </div>
        </ModalWindow>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export default EventAgendaPublic;
